import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon, Save as SaveIcon } from "@mui/icons-material";
import { apiGetSettings, apiCreateSetting, apiUpdateSetting } from "../../../graphQL/settings";
import { UI_TOGGLES, AREA_ORDER, DEFAULTS } from "../../../config/uiToggles";
import pkg from "../../../../package.json";

/* Indigo palette (per house design guidelines — no black). */
const INDIGO_900 = "#1A237E";
const INDIGO_500 = "#3F51B5";
const INDIGO_600 = "#3949AB";
const INK = "#1A1F36";
const TINT = "#F4F5FB";

const extractToggleArray = (setting) => {
  const payload = setting?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.toggles)) return payload.toggles;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const pickLatest = (settings) => {
  if (!Array.isArray(settings) || settings.length === 0) return null;
  const ts = (v) => {
    const p = Date.parse(String(v ?? ""));
    return Number.isFinite(p) ? p : Number(v ?? 0) || 0;
  };
  return settings.reduce(
    (a, b) => (ts(b?.updatedAt ?? b?.createdAt) >= ts(a?.updatedAt ?? a?.createdAt) ? b : a),
    settings[0]
  );
};

// Resolve a remote toggle array over the hardcoded DEFAULTS map.
const resolveValues = (toggleArray) => {
  const values = {};
  UI_TOGGLES.forEach((t) => {
    values[t.key] = DEFAULTS[t.key] !== false;
  });
  (toggleArray || []).forEach((entry) => {
    if (!entry?.key) return;
    if (Object.prototype.hasOwnProperty.call(values, entry.key)) {
      values[entry.key] = entry.isActive !== false;
    }
  });
  return values;
};

const detectSuperAdmin = (user) => {
  const role = String(user?.role || "").toLowerCase();
  const domainList = Array.isArray(user?.domainList) ? user.domainList : [];
  const hasMultiDomainAccess =
    domainList.length > 1 || domainList.some((d) => String(d).toLowerCase() === "all");
  return (
    user?.isSuperAdmin === true ||
    role === "superadmin" ||
    role === "super_admin" ||
    role === "sysadmin" ||
    role.includes("super") ||
    hasMultiDomainAccess
  );
};

/**
 * "Visible features" settings tab — turn UI buttons/features on and off.
 * Writes a `uiToggles` / `app` setting whose data is
 * { toggles: [{ key, isActive }], uiTogglesProvenance: {...} }.
 * User scope writes with authorId; super-admins can switch to domain scope
 * (writes with domain, sets the org-wide default).
 */
export default function VisibleFeaturesTab({ authorId }) {
  const user = useSelector((state) => state.auth.user);
  const domain = String(user?.domain || "").trim();
  const isSuperAdmin = detectSuperAdmin(user);

  const [scope, setScope] = useState("user"); // "user" | "domain"
  const [values, setValues] = useState(() => resolveValues([]));
  const [setting, setSetting] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const grouped = useMemo(() => {
    const map = {};
    UI_TOGGLES.forEach((t) => {
      (map[t.area] = map[t.area] || []).push(t);
    });
    return map;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const input =
        scope === "domain"
          ? { schemaType: "uiToggles", type: "app", domain: domain || undefined }
          : { schemaType: "uiToggles", type: "app", authorId };
      const result = await apiGetSettings(input);
      const latest = pickLatest(result?.settings);
      setSetting(latest);
      setValues(resolveValues(extractToggleArray(latest)));
      setDirty(false);
    } catch (e) {
      console.error("Failed to load UI toggle settings:", e);
      setValues(resolveValues([]));
    } finally {
      setLoading(false);
    }
  }, [scope, authorId, domain]);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggle = (key) => {
    setDirty(true);
    setValues((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const toggles = UI_TOGGLES.map((t) => ({ key: t.key, isActive: values[t.key] !== false }));
      const data = {
        toggles,
        uiTogglesProvenance: {
          source: "webapp:settings-dialog",
          scope,
          updatedBy: authorId || null,
          updatedAt: new Date().toISOString(),
          appVersion: pkg.version || null,
        },
      };

      // Only isReset/default docs carry a synthetic id; never update those.
      const isSynthetic = typeof setting?.id === "string" && setting.id.startsWith("default_");

      // Dry-run log before the (first) live write.
      // eslint-disable-next-line no-console
      console.info("[uiToggles] saving setting", {
        mode: setting && !isSynthetic ? "update" : "create",
        scope,
        target: scope === "domain" ? { domain } : { authorId },
        activeCount: toggles.filter((t) => t.isActive).length,
        total: toggles.length,
      });

      const scopeFields = scope === "domain" ? { domain } : { authorId };
      if (setting && !isSynthetic) {
        await apiUpdateSetting({ id: setting.id, schemaType: "uiToggles", type: "app", ...scopeFields, data });
      } else {
        await apiCreateSetting({ schemaType: "uiToggles", type: "app", ...scopeFields, data });
      }

      // Reflect immediately for the next paint / reload.
      try {
        if (scope === "user") {
          const resolved = {};
          toggles.forEach((t) => { resolved[t.key] = t.isActive; });
          localStorage.setItem("cs_ui_toggles", JSON.stringify({ ...DEFAULTS, ...resolved }));
        }
      } catch {
        /* ignore */
      }

      setDirty(false);
      await load();
    } catch (e) {
      console.error("Failed to save UI toggle settings:", e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 1.5, flexWrap: "wrap" }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: INDIGO_900 }}>Visible features</Typography>
          <Typography sx={{ fontSize: 12, color: "#5A6182" }}>
            Turn UI buttons and panels on or off. Hidden controls disappear from the app.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isSuperAdmin && (
            <TextField
              select
              size="small"
              label="Scope"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              sx={{ minWidth: 160, "& .MuiInputBase-root": { fontSize: 12 } }}
            >
              <MenuItem value="user" sx={{ fontSize: 12 }}>My settings</MenuItem>
              <MenuItem value="domain" sx={{ fontSize: 12 }}>{`Domain default (${domain || "—"})`}</MenuItem>
            </TextField>
          )}
          <Button
            size="small"
            variant="contained"
            startIcon={<SaveIcon sx={{ fontSize: 14 }} />}
            onClick={handleSave}
            disabled={!dirty || saving}
            sx={{
              textTransform: "none",
              fontSize: 12,
              fontWeight: 600,
              backgroundColor: INDIGO_500,
              "&:hover": { backgroundColor: INDIGO_600 },
            }}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Box>

      {scope === "domain" && (
        <Typography sx={{ fontSize: 11, color: "#5A6182", mb: 1 }}>
          Domain-level defaults apply to every user in {domain || "this domain"} who has not set their own preference.
        </Typography>
      )}

      {loading ? (
        <Typography sx={{ textAlign: "center", py: 4, fontSize: 13, color: "text.secondary" }}>Loading...</Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {AREA_ORDER.map((area) => {
            const items = grouped[area];
            if (!items || items.length === 0) return null;
            const onCount = items.filter((t) => values[t.key] !== false).length;
            return (
              // Collapsed by default (no defaultExpanded) so every area name is
              // visible at a glance; the user expands only the one they want.
              <Accordion
                key={area}
                disableGutters
                elevation={0}
                square={false}
                sx={{
                  border: "1px solid #E8EAF6",
                  borderRadius: 2,
                  overflow: "hidden",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ fontSize: 18, color: INDIGO_500 }} />}
                  sx={{
                    minHeight: 0,
                    backgroundColor: TINT,
                    "& .MuiAccordionSummary-content": {
                      my: 0.75,
                      alignItems: "center",
                      justifyContent: "space-between",
                      pr: 1,
                    },
                  }}
                >
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: INDIGO_900, textTransform: "uppercase", letterSpacing: 0.4 }}>
                    {area}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${onCount}/${items.length} on`}
                    sx={{
                      height: 18,
                      fontSize: 10,
                      fontWeight: 600,
                      color: onCount === 0 ? "#9AA0BF" : INDIGO_600,
                      backgroundColor: "#E8EAF6",
                    }}
                  />
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0, borderTop: "1px solid #E8EAF6" }}>
                  {items.map((t, i) => (
                    <Box
                      key={t.key}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 1.5,
                        py: 0.5,
                        borderTop: i === 0 ? "none" : "1px solid #F1F2FA",
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13, color: INK }}>{t.label}</Typography>
                        <Typography sx={{ fontSize: 10, color: "#9AA0BF", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                          {t.key}
                        </Typography>
                      </Box>
                      <Switch
                        size="small"
                        checked={values[t.key] !== false}
                        onChange={() => handleToggle(t.key)}
                      />
                    </Box>
                  ))}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
